/**
 * RegionalAirshedLayer — airshed polygons or 500 km circular buffers.
 *
 * Renders one polygon (preferred) or circular buffer (fallback) per
 * displaced plant, colored by that plant's regional-scale DALYs share
 * on a sequential (Viridis-like) ramp. A small plant dot is overlaid
 * on top so customers can see which plant owns which airshed.
 *
 * Overlap handling:
 *   - Per-polygon TOOLTIP (hover): shows only the top-most plant —
 *     kept for quick scanning
 *   - Map-level CLICK popup: finds every visible plant whose airshed
 *     covers the clicked point and lists all of them. Fixes the
 *     "polygon soup" issue where many airsheds overlap in one pixel
 *     and the click-target is non-deterministic
 *
 * Top-N toggle:
 *   - For projects with many displaced plants the map becomes a dark
 *     blob of overlapping polygons. Default to top 10 plants by
 *     regional value; customer can toggle to "all" via the header
 *     control. Plants below the cut still contribute to the project
 *     total (shown in the SpatialBreakdownBar header) but don't
 *     render on the map.
 */

import { Fragment, useMemo, useState } from "react";
import {
  Circle,
  CircleMarker,
  Polygon,
  Popup,
  Tooltip,
  useMapEvents,
} from "react-leaflet";
import type { LatLng, LatLngExpression } from "leaflet";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import type {
  AirshedFeatureCollection,
  DisplacedGeneratorEnriched,
} from "../../model/domain/types";
import {
  MetricKey,
  METRIC_LABEL,
  approxAirshedPopulation,
  dominantRegionalPollutants,
  fmtMetric,
  fmtPopulation,
  getFuelColor,
  getFuelDisplayName,
  pickMetric,
  stateName,
} from "../helpers";

// ──────────────────────────────────────────────────────────────────────
// Viridis-ish sequential color ramp (t ∈ [0,1])
// ──────────────────────────────────────────────────────────────────────

function colorForShare(t: number): string {
  const v = Math.max(0, Math.min(1, t));
  const stops: Array<[number, number, number]> = [
    [68, 1, 84],     // dark purple
    [59, 82, 139],   // blue
    [33, 145, 140],  // teal
    [94, 201, 98],   // green
    [253, 231, 37],  // yellow
  ];
  const pos = v * (stops.length - 1);
  const i = Math.min(Math.floor(pos), stops.length - 2);
  const frac = pos - i;
  const [r1, g1, b1] = stops[i];
  const [r2, g2, b2] = stops[i + 1];
  const r = Math.round(r1 + (r2 - r1) * frac);
  const g = Math.round(g1 + (g2 - g1) * frac);
  const b = Math.round(b1 + (b2 - b1) * frac);
  return `rgb(${r},${g},${b})`;
}

export const RAMP_SAMPLE = [0, 0.25, 0.5, 0.75, 1].map(colorForShare);

// ──────────────────────────────────────────────────────────────────────
// GeoJSON ↔ Leaflet coordinate conversion
// ──────────────────────────────────────────────────────────────────────

function toLatLngRing(coords: number[][]): LatLngExpression[] {
  return coords.map(([lon, lat]) => [lat, lon] as LatLngExpression);
}

function toLeafletPolygon(
  geometry: { type: string; coordinates: any },
): LatLngExpression[] | LatLngExpression[][] {
  if (geometry.type === "Polygon") {
    return (geometry.coordinates as number[][][]).map(toLatLngRing);
  }
  const mp = geometry.coordinates as number[][][][];
  return mp.map((poly) => poly.map(toLatLngRing)).flat();
}

// Rough great-circle distance for the circular-buffer fallback check
// (used only when an airshed polygon is not available for a plant).
function kmBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// ──────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────

interface Props {
  plants: DisplacedGeneratorEnriched[];
  metric: MetricKey;
  airsheds?: AirshedFeatureCollection | null;
  onPlantHover?: (plantId: string | number | null) => void;
}

const DEFAULT_TOP_N = 10;

export function RegionalAirshedLayer({
  plants,
  metric,
  airsheds,
  onPlantHover,
}: Props) {
  const [limit, setLimit] = useState<number | "all">(DEFAULT_TOP_N);
  // Click a plant dot to isolate that plant's airshed (dim the rest).
  // Click again (or the "show all" button in the popup) to clear.
  const [isolatedId, setIsolatedId] = useState<string | number | null>(null);

  const airshedByPlantId = useMemo(() => {
    const m = new Map<string, { type: string; coordinates: any }>();
    if (airsheds?.features) {
      for (const f of airsheds.features) {
        const id = String(f.properties?.plant_id);
        if (id && f.geometry) m.set(id, f.geometry);
      }
    }
    return m;
  }, [airsheds]);

  // Sort plants by regional value desc so top-N picks the biggest contributors.
  const sortedPlants = useMemo(
    () =>
      [...plants]
        .filter((p) => p.latitude && p.longitude)
        .sort(
          (a, b) =>
            pickMetric(b, "regional", metric) - pickMetric(a, "regional", metric),
        ),
    [plants, metric],
  );

  const visiblePlants = useMemo(
    () => (limit === "all" ? sortedPlants : sortedPlants.slice(0, limit)),
    [sortedPlants, limit],
  );

  const maxValue = useMemo(
    () =>
      visiblePlants.reduce(
        (acc, p) => Math.max(acc, pickMetric(p, "regional", metric)),
        0,
      ),
    [visiblePlants, metric],
  );

  return (
    <>
      <TopNToggle
        nTotal={sortedPlants.length}
        limit={limit}
        onChange={setLimit}
      />

      {isolatedId !== null && (
        <IsolationBanner
          plantName={
            visiblePlants.find((p) => String(p.plant_id) === String(isolatedId))
              ?.plant_name ?? "—"
          }
          onClear={() => setIsolatedId(null)}
        />
      )}

      {visiblePlants.map((p) => {
        const regionalValue = pickMetric(p, "regional", metric);
        const share = maxValue > 0 ? regionalValue / maxValue : 0;
        const color = colorForShare(share);
        const fuelColor = getFuelColor(p.fuel_type);
        const polygonGeom = airshedByPlantId.get(String(p.plant_id));
        const isIsolated = isolatedId !== null;
        const isThisPlantIsolated =
          isIsolated && String(isolatedId) === String(p.plant_id);

        // When a plant is isolated, the others fade way back and the
        // isolated one gets a heavier fill + brighter outline.
        const fillOpacity = isIsolated
          ? isThisPlantIsolated
            ? 0.45
            : 0.03
          : 0.18;
        const strokeOpacity = isIsolated
          ? isThisPlantIsolated
            ? 1.0
            : 0.15
          : 0.7;
        const strokeWeight = isThisPlantIsolated ? 2.5 : 1.5;

        const hoverContent = (
          <div className="text-sm min-w-[220px] text-gray-800">
            <p className="font-semibold text-gray-900 text-[15px] mb-1">
              {p.plant_name}
            </p>
            <p className="text-gray-600 text-xs mb-1">
              <span
                className="inline-block w-2 h-2 rounded-full mr-1"
                style={{ backgroundColor: fuelColor }}
              />
              {getFuelDisplayName(p.fuel_type)} · {p.county}, {stateName(p.state)}
            </p>
            <div className="text-gray-900 font-semibold">
              {fmtMetric(regionalValue, metric)} {METRIC_LABEL[metric]} regional
            </div>
            <p className="mt-1 text-[11px] text-gray-500 leading-tight">
              Color shows this plant's regional share across its whole
              airshed — it does not change with position. Click the
              source dot to isolate this plant's airshed.
            </p>
          </div>
        );

        const eventHandlers = onPlantHover
          ? {
              mouseover: () => onPlantHover(p.plant_id),
              mouseout: () => onPlantHover(null),
            }
          : undefined;

        return (
          <Fragment key={`airshed-wrap-${p.plant_id}`}>
            {polygonGeom ? (
              <Polygon
                key={`airshed-${p.plant_id}`}
                positions={toLeafletPolygon(polygonGeom) as any}
                pathOptions={{
                  fillColor: color,
                  color,
                  weight: strokeWeight,
                  fillOpacity,
                  opacity: strokeOpacity,
                }}
                eventHandlers={eventHandlers}
              >
                <Tooltip sticky>{hoverContent}</Tooltip>
              </Polygon>
            ) : (
              <Circle
                key={`buffer-${p.plant_id}`}
                center={[p.latitude, p.longitude]}
                radius={500_000}
                pathOptions={{
                  fillColor: color,
                  color,
                  weight: strokeWeight,
                  fillOpacity: Math.max(fillOpacity - 0.06, 0.02),
                  opacity: strokeOpacity * 0.85,
                  dashArray: "6 4",
                }}
                eventHandlers={eventHandlers}
              >
                <Tooltip sticky>{hoverContent}</Tooltip>
              </Circle>
            )}

            <CircleMarker
              key={`plant-dot-${p.plant_id}`}
              center={[p.latitude, p.longitude]}
              radius={isThisPlantIsolated ? 6 : 4}
              pathOptions={{
                fillColor: fuelColor,
                color: isThisPlantIsolated ? "#facc15" : "#ffffff",
                weight: isThisPlantIsolated ? 2 : 1,
                fillOpacity: 0.95,
                opacity: 1,
              }}
              eventHandlers={{
                ...(eventHandlers ?? {}),
                click: () => {
                  setIsolatedId((prev) =>
                    String(prev) === String(p.plant_id)
                      ? null
                      : p.plant_id,
                  );
                },
              }}
            >
              <Tooltip direction="top" offset={[0, -6]}>
                <span className="font-semibold text-[13px] text-gray-900">
                  {p.plant_name}
                  <span className="ml-1 text-gray-500 text-[11px] font-normal">
                    {isThisPlantIsolated ? "(click to show all)" : "(click to isolate)"}
                  </span>
                </span>
              </Tooltip>
            </CircleMarker>
          </Fragment>
        );
      })}

      {/* Map-level click popup — lists every visible plant whose airshed
          (or 500 km circular fallback) contains the clicked point. */}
      <MultiPlantClickPopup
        plants={visiblePlants}
        metric={metric}
        airshedByPlantId={airshedByPlantId}
      />
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Isolation banner — small pill under the map header when a plant is
// isolated. Gives a clear way to exit the isolated view without
// hunting for the plant dot to click again.
// ──────────────────────────────────────────────────────────────────────

function IsolationBanner({
  plantName,
  onClear,
}: {
  plantName: string;
  onClear: () => void;
}) {
  return (
    <div
      className="leaflet-top leaflet-left"
      style={{ pointerEvents: "auto" }}
    >
      <div
        className="leaflet-control bg-amber-500/10 border border-amber-400/40 rounded-md shadow-md px-3 py-1.5 text-xs text-gray-800 flex items-center gap-3"
        style={{ margin: "10px 0 0 10px" }}
      >
        <span>
          Showing only{" "}
          <span className="font-semibold text-gray-900">{plantName}</span>&apos;s airshed
        </span>
        <button
          type="button"
          onClick={onClear}
          className="text-quantum-accent hover:underline font-medium"
        >
          show all
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Top-N toggle
// ──────────────────────────────────────────────────────────────────────

function TopNToggle({
  nTotal,
  limit,
  onChange,
}: {
  nTotal: number;
  limit: number | "all";
  onChange: (l: number | "all") => void;
}) {
  if (nTotal <= DEFAULT_TOP_N) return null;
  return (
    <div
      className="leaflet-top leaflet-right"
      style={{ pointerEvents: "auto" }}
    >
      <div
        className="leaflet-control bg-white/95 rounded-md shadow-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700"
        style={{ margin: "10px 10px 0 0" }}
      >
        <span className="mr-2 text-gray-500">
          Showing{" "}
          <span className="font-semibold text-gray-900">
            {limit === "all" ? nTotal : Math.min(limit as number, nTotal)}
          </span>
          {" "}of {nTotal} plants
        </span>
        <button
          type="button"
          onClick={() => onChange(limit === "all" ? DEFAULT_TOP_N : "all")}
          className="text-quantum-accent hover:underline font-medium"
        >
          {limit === "all" ? `show top ${DEFAULT_TOP_N}` : "show all"}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Map-level click → combined popup
// ──────────────────────────────────────────────────────────────────────

function MultiPlantClickPopup({
  plants,
  metric,
  airshedByPlantId,
}: {
  plants: DisplacedGeneratorEnriched[];
  metric: MetricKey;
  airshedByPlantId: Map<string, { type: string; coordinates: any }>;
}) {
  const [clicked, setClicked] = useState<LatLng | null>(null);

  useMapEvents({
    click: (e) => setClicked(e.latlng),
  });

  const matches = useMemo(() => {
    if (!clicked) return [];
    const pt = { type: "Point" as const, coordinates: [clicked.lng, clicked.lat] };
    const out: Array<{ p: DisplacedGeneratorEnriched; value: number; polygon: boolean }> = [];
    for (const p of plants) {
      const geom = airshedByPlantId.get(String(p.plant_id));
      let hit = false;
      if (geom) {
        try {
          hit = booleanPointInPolygon(pt, {
            type: "Feature",
            geometry: geom as any,
            properties: {},
          });
        } catch {
          hit = false;
        }
      } else if (p.latitude && p.longitude) {
        hit =
          kmBetween(
            { lat: clicked.lat, lng: clicked.lng },
            { lat: p.latitude, lng: p.longitude },
          ) < 500;
      }
      if (hit) {
        out.push({ p, value: pickMetric(p, "regional", metric), polygon: !!geom });
      }
    }
    out.sort((a, b) => b.value - a.value);
    return out;
  }, [clicked, plants, airshedByPlantId, metric]);

  if (!clicked) return null;

  const pop = matches.length > 0 ? approxAirshedPopulation(matches[0].p.state) : null;

  return (
    <Popup
      position={clicked}
      className="spatial-popup"
      eventHandlers={{ remove: () => setClicked(null) }}
      maxWidth={320}
    >
      <div className="text-sm text-gray-800 min-w-[260px]">
        {matches.length === 0 ? (
          <p className="text-gray-600 text-xs">
            No plant airshed reaches here. Click inside a colored polygon
            to see the plants whose downwind impact lands at that point.
          </p>
        ) : (
          <>
            <p className="font-semibold text-gray-900 mb-1.5">
              {matches.length} {matches.length === 1 ? "plant" : "plants"}{" "}
              reach this point
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
              Regional {METRIC_LABEL[metric]} contribution
            </p>
            <ul className="space-y-1">
              {matches.slice(0, 8).map(({ p, value, polygon }) => {
                const fuelColor = getFuelColor(p.fuel_type);
                const pollutants = dominantRegionalPollutants(
                  p.impacts_by_category,
                );
                return (
                  <li
                    key={`match-${p.plant_id}`}
                    className="border-b border-gray-100 last:border-0 pb-1 last:pb-0"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-900 leading-tight">
                        <span
                          className="inline-block w-2 h-2 rounded-full mr-1 -mb-0.5"
                          style={{ backgroundColor: fuelColor }}
                        />
                        {p.plant_name}
                      </span>
                      <span className="text-gray-900 font-semibold whitespace-nowrap">
                        {fmtMetric(value, metric)}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 leading-tight">
                      {getFuelDisplayName(p.fuel_type)} ·{" "}
                      {p.county ? `${p.county}, ` : ""}
                      {stateName(p.state)}
                      {!polygon && " · 500 km buffer"}
                      {pollutants.length > 0 && (
                        <>
                          {" · "}
                          {pollutants
                            .slice(0, 2)
                            .map(
                              (x) =>
                                `${Math.round(x.share * 100)}% ${x.label}`,
                            )
                            .join(", ")}
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {matches.length > 8 && (
              <p className="mt-1 text-[11px] text-gray-500">
                + {matches.length - 8} more plants also reach here
              </p>
            )}
            {pop && pop.millions > 0 && (
              <p className="mt-2 pt-2 border-t border-gray-200 text-[11px] text-gray-500 leading-snug">
                Top plant's airshed reaches {fmtPopulation(pop.millions)}{" "}
                across ~{pop.nStates}{" "}
                {pop.nStates === 1 ? "state" : "states"}
              </p>
            )}
          </>
        )}
      </div>
    </Popup>
  );
}
